import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { electronApis } from '@/features/electron-api';
import {
  AXON_MCP_SERVERS_QUERY_KEY,
  useLocalPathCheckQuery,
  useMcpServersQuery,
  useUpdateMcpServerMutation,
} from '@/features/settings/sub-settings/settings-local-mcp.query';

import type { LocalPathProblem } from '@electron/shared/local-mcp.types';
import type { ServerEntry, ServerEntryInput } from '@littlebird/axon-mcp';

export interface LocalPathFailure {
  problem: LocalPathProblem;
  path: string;
}

function pathsFromTransport(transport: ServerEntry['transport']): string[] {
  return 'args' in transport ? [...(transport.args ?? [])] : [];
}

function withPathArgs(
  server: ServerEntry,
  args: readonly string[]
): Partial<ServerEntryInput> {
  if (server.transport.kind === 'http') return {};
  return { transport: { ...server.transport, args: [...args] } };
}

export function useLocalPathsConfiguration(serverSlug: string | undefined) {
  const serversQuery = useMcpServersQuery();
  const update = useUpdateMcpServerMutation();
  const queryClient = useQueryClient();
  const [rejectedPick, setRejectedPick] = useState<LocalPathFailure | null>(
    null
  );

  const server = serversQuery.data?.find(
    (candidate) => candidate.slug === serverSlug
  );
  const paths = server ? pathsFromTransport(server.transport) : [];
  const savedCheck = useLocalPathCheckQuery(
    server?.slug,
    paths,
    serversQuery.dataUpdatedAt
  );
  const savedFailure =
    savedCheck.data && !savedCheck.data.ok ? savedCheck.data : null;

  const addPath = () => {
    void electronApis.axonMcp
      .pickDirectory()
      .then(async (picked) => {
        setRejectedPick(null);
        if (!picked) return;
        // Re-reads the server from the query cache instead of closing over
        // the `server`/`paths` captured when the picker opened — the picker
        // dialog can outlive that render, so acting on the stale snapshot
        // could silently drop a change made elsewhere while it was open.
        const latestServers = queryClient.getQueryData<ServerEntry[]>(
          AXON_MCP_SERVERS_QUERY_KEY
        );
        const latestServer = latestServers?.find(
          (candidate) => candidate.slug === serverSlug
        );
        if (!latestServer) return;
        const latestPaths = pathsFromTransport(latestServer.transport);
        if (latestPaths.includes(picked)) return;

        const check = await electronApis.axonMcp.checkLocalPaths([picked]);
        if (!check.ok) {
          setRejectedPick({ problem: check.problem, path: check.path });
          return;
        }
        update.mutate({
          slug: latestServer.slug,
          changes: withPathArgs(latestServer, [...latestPaths, picked]),
        });
      })
      .catch(() => toast.error('Couldn’t check that folder'));
  };

  const removePath = (path: string) => {
    if (!server) return;
    setRejectedPick(null);
    update.mutate({
      slug: server.slug,
      changes: withPathArgs(
        server,
        paths.filter((candidate) => candidate !== path)
      ),
    });
  };

  return {
    serversQuery,
    server,
    paths,
    failure: rejectedPick ?? savedFailure,
    saving: update.isPending,
    addPath,
    removePath,
  };
}
